import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPasswordThunk } from "reduxStore/auth/thunks";
import { useAppSelector } from "hooks/useAppSelector";
import { authStatusSelector } from "reduxStore/auth/selectors";
import { getValidationSchema, initialValues } from "./config";
import { IResetPasswordCredentials } from "types/auth";
import { useAppDispatch } from "hooks/useAppDispatch";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Messages, NotificationSeverity } from "helpers/constants";
import Notification from "utils/Notification";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { AuthContainer } from "components/Layout/AuthContainer";
import { Password } from "components/Password";

const ResetPasswordWrapper: React.FC = () => {
  const statuses = useAppSelector(authStatusSelector);
  const navigate = useNavigate();
  const error = statuses["auth/resetPasswordThunk"].error;
  const loading = statuses["auth/resetPasswordThunk"].pending;
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const handleSubmit = (values: IResetPasswordCredentials) => {
    dispatch(resetPasswordThunk(values));
    dispatch(
      showNotificationThunk({
        message: Messages.successPassword,
        severity: NotificationSeverity.success,
      }),
    );
    navigate("/");
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema,
    onSubmit: (values) => {
      const data = {
        password: values.password,
        username: searchParams.get("email") ?? "",
        code: searchParams.get("code") ?? "",
      };
      handleSubmit(data);
    },
  });

  return (
    <AuthContainer loading={loading} loadingText="Resetting password...">
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6">
          Reset Password
        </Typography>
        <FormikProvider value={formik}>
          <Box
            component="form"
            noValidate
            onSubmit={formik.handleSubmit}
            sx={{ mt: 1 }}
          >
            <Password
              id="password"
              name="password"
              label="Password"
              required
              onChange={formik.handleChange}
            />
            <ErrorMessage name="password" component={StyledFormicError} />
            <Password
              id="repeatPassword"
              name="repeatPassword"
              label="Repeat password"
              required
              onChange={formik.handleChange}
            />
            <ErrorMessage name="repeatPassword" component={StyledFormicError} />
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                disabled={loading}
                type="submit"
                fullWidth
                color="primary"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Submit
              </Button>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>

            {error && (
              <StyledFormicError>
                Invalid code provided, please start the reset password process
                again.
              </StyledFormicError>
            )}
          </Box>
        </FormikProvider>
        <Notification onClose={() => {}} />
      </Box>
    </AuthContainer>
  );
};

export default ResetPasswordWrapper;
